/**
 * Utility Functions for Formatting
 * دوال مساعدة للتنسيق
 *
 * Protocols Applied:
 * - P-SIMPLE-001: Keep it simple
 * - P-RTL-001: RTL support with Arabic locale
 */

/**
 * Format number as currency (KWD - Kuwaiti Dinar)
 * @param {number} amount - Amount to format
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount) => {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return '0.000 د.ك'
  }

  return parseFloat(amount).toLocaleString('ar-KW', {
    style: 'currency',
    currency: 'KWD',
    minimumFractionDigits: 3,
    maximumFractionDigits: 3
  })
}

/**
 * Format date to Arabic locale
 * @param {string|Date} date - Date to format
 * @param {Object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date string
 */
export const formatDate = (date, options = {}) => {
  if (!date) {
    return '-'
  }

  const defaultOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    ...options
  }

  return new Date(date).toLocaleDateString('ar-EG', defaultOptions)
}

/**
 * Format date to short format (DD/MM/YYYY)
 * @param {string|Date} date - Date to format
 * @returns {string} Formatted date string
 */
export const formatDateShort = (date) => {
  if (!date) {
    return '-'
  }

  return new Date(date).toLocaleDateString('ar-EG')
}

/**
 * Format date as DD-MM-YY (compact format)
 * @param {string|Date} date - Date to format
 * @returns {string} Formatted date string (DD-MM-YY)
 */
export const formatDateDDMMYY = (date) => {
  if (!date) {
    return '-'
  }

  const d = new Date(date)
  const day = String(d.getDate()).padStart(2, '0')
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const year = String(d.getFullYear()).slice(-2)

  return `${day}-${month}-${year}`
}

/**
 * Format date and time as DD-MM-YY H:i (24-hour format)
 * @param {string|Date} date - Date to format
 * @returns {string} Formatted date and time string (DD-MM-YY H:i)
 */
export const formatDateTimeShort = (date) => {
  if (!date) {
    return '-'
  }

  const d = new Date(date)
  const day = String(d.getDate()).padStart(2, '0')
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const year = String(d.getFullYear()).slice(-2)
  const hours = String(d.getHours()).padStart(2, '0')
  const minutes = String(d.getMinutes()).padStart(2, '0')

  return `${day}-${month}-${year} ${hours}:${minutes}`
}

/**
 * Format time to 12-hour format with AM/PM
 * @param {string|Date} date - Date to format
 * @returns {string} Formatted time string
 */
export const formatTime = (date) => {
  if (!date) {
    return '-'
  }

  return new Date(date).toLocaleTimeString('ar-EG', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  })
}

/**
 * Format phone number
 * @param {string} phone - Phone number
 * @returns {string} Formatted phone number
 */
export const formatPhone = (phone) => {
  if (!phone) {
    return '-'
  }

  // Remove non-numeric characters
  const cleaned = phone.replace(/\D/g, '')

  // Format as: 0XX XXXX XXXX (Egyptian format)
  if (cleaned.length === 11 && cleaned.startsWith('0')) {
    return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 7)} ${cleaned.slice(7)}`
  }

  return phone
}

/**
 * Format number with thousands separator
 * @param {number} num - Number to format
 * @returns {string} Formatted number string
 */
export const formatNumber = (num) => {
  if (num === null || num === undefined || isNaN(num)) {
    return '0'
  }

  return parseFloat(num).toLocaleString('ar-EG')
}

/**
 * Format percentage
 * @param {number} value - Value to format as percentage
 * @param {number} decimals - Number of decimal places (default: 0)
 * @returns {string} Formatted percentage string
 */
export const formatPercentage = (value, decimals = 0) => {
  if (value === null || value === undefined || isNaN(value)) {
    return '0%'
  }

  return `${parseFloat(value).toFixed(decimals)}%`
}

/**
 * Format file size in bytes to human readable format
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted file size
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 بايت'
  if (!bytes) return '-'

  const k = 1024
  const sizes = ['بايت', 'ك.ب', 'م.ب', 'ج.ب', 'ت.ب']
  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`
}
